import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset, Login as LoginIcon } from '@mui/icons-material';
import loginBanner from '../assets/login-banner.png'; 
// --- NUEVO: IMPORTAMOS EL COMPONENTE DE MICRÓFONO ---
import FloatingMic from '../components/FloatingMic';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [openReset, setOpenReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState({ loading: false, msg: '', error: false });

  const { login } = useAuth();
  const navigate = useNavigate();

  // --- TU LÓGICA DE AUTENTICACIÓN ORIGINAL (INTACTA) ---
  const handleSubmit = async (e) => {
    e.preventDefault(); 
    if (isSubmitting) return;
    if (!email || !password) {
        setError("Por favor complete todos los campos");
        return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate('/dashboard', { replace: true });
      } else {
        setError('Credenciales incorrectas.');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      setError('Error de conexión con el servidor.');
      setIsSubmitting(false);
    }
  };

  const handleOpenReset = () => {
      setResetEmail(email); 
      setOpenReset(true);
      setResetStatus({ loading: false, msg: '', error: false });
  };

  // --- TU LÓGICA DE RECUPERACIÓN ORIGINAL (INTACTA) ---
  const handleRecoverPassword = async () => {
      if (!resetEmail) {
          setResetStatus({ loading: false, msg: 'Ingrese su correo.', error: true });
          return;
      }
      setResetStatus({ loading: true, msg: '', error: false });
      try {
          // Nota: Asegúrate de que esta ruta /api/recover-password exista en tu backend nuevo
          const response = await fetch(`${API_URL}/api/recover-password`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: resetEmail })
          });
          const data = await response.json();
          if (response.ok) {
              setResetStatus({ 
                  loading: false, 
                  msg: 'Si el correo existe, se enviaron las instrucciones.', 
                  error: false 
              });
              setTimeout(() => setOpenReset(false), 3500); 
          } else {
              throw new Error(data.message || 'Error al procesar');
          }
      } catch (error) {
          console.error(error);
          setResetStatus({ loading: false, msg: 'Error de conexión.', error: true });
      }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f2f5' }}>
      <Container component="main" maxWidth="xs">
        <Paper elevation={6} sx={{ borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box sx={{ height: 160, width: '100%', backgroundImage: `url(${loginBanner})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '60%', background: 'linear-gradient(to top, rgba(255,255,255,1), transparent)' }}/>
            </Box>
            <Box sx={{ p: 4, pt: 2 }}>
                <Typography component="h1" variant="h5" fontWeight="bold" color="primary" align="center" gutterBottom>Bienvenido</Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    {/* --- CAMBIO AQUÍ: Usamos FloatingMic para el correo (Micrófono a la izquierda) --- */}
                    <TextField
						label="Correo Electrónico"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						margin="normal"
						required
						fullWidth
						id="email"
						name="email"
						autoComplete="email"
						autoFocus
					/>

                    {/* --- CONTRASEÑA ORIGINAL (Sin micrófono, solo Ojo a la derecha) --- */}
                    <TextField 
                        margin="normal" 
                        required 
                        fullWidth 
                        name="password" 
                        label="Contraseña" 
                        type={showPassword ? 'text' : 'password'} 
                        id="password" 
                        autoComplete="current-password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        disabled={isSubmitting} 
                        InputProps={{ 
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ) 
                        }} 
                    />

                    <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 3, mb: 2, borderRadius: 2, height: 48 }} disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit"/> : <LoginIcon />}>{isSubmitting ? 'Ingresando...' : 'Iniciar Sesión'}</Button>
                    <Box sx={{ textAlign: 'center', mt: 1 }}><Link component="button" type="button" variant="body2" onClick={handleOpenReset} underline="hover">¿Olvidaste tu contraseña?</Link></Box>
                </Box>
            </Box>
        </Paper>
      </Container>
      
      {/* --- MODAL DE RECUPERACIÓN ORIGINAL --- */}
      <Dialog open={openReset} onClose={() => setOpenReset(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{display:'flex', alignItems:'center', gap:1}}><LockReset color="primary"/> Recuperar Acceso</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{mb:2}}>Ingresa tu correo electrónico asociado.</DialogContentText>
          <TextField autoFocus margin="dense" id="resetEmail" label="Correo Electrónico" type="email" fullWidth variant="outlined" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} />
          {resetStatus.msg && (<Alert severity={resetStatus.error ? "error" : "success"} sx={{ mt: 2 }}>{resetStatus.msg}</Alert>)}
        </DialogContent>
        <DialogActions sx={{p:2}}><Button onClick={() => setOpenReset(false)} color="inherit">Cancelar</Button><Button onClick={handleRecoverPassword} variant="contained" disabled={resetStatus.loading}>Enviar Enlace</Button></DialogActions>
      </Dialog>
    </Box>
  );
};
export default LoginPage;